import { Card, Input, useToast } from "@design";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Search } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { AgentResponse } from "@/api";
import {
  attachSkillsToAgentMutation,
  detachSkillFromAgent,
  errorMessage,
  getAgentQueryKey,
  listAgentConnectorsOptions,
  listAgentConnectorsQueryKey,
  listAgentSkillsOptions,
  listAgentSkillsQueryKey,
  listConnectorsOptions,
  listDataSourcesOptions,
  listSkillsOptions,
  listToolsOptions,
  setAgentConnectors,
  updateAgentMutation,
} from "@/api";
import { SaveBar } from "@/components/build/SaveBar";
import { Text } from "@/components/Text";
import { ConnectorsSection, ExtensionGroups, KnowledgeSection, UngroupedSkills } from "./CapabilitySections";

/**
 * The agent's Capabilities tab. Matches the old UI's model (EditAgentModal's
 * Capabilities tab): a search box, a "baseline capabilities" note, and
 * capabilities grouped by their parent Extension as toggle-on-click cards —
 * plus a collapsible "Other tools" and a Knowledge (data sources) group.
 *
 * Save model: the whole selection is held in local state and saved in one
 * agent PATCH (`toolConfigs` + `dataCollectionIds` are replace-semantic) plus
 * per-skill attach/detach calls — so there's ONE save action for the whole
 * tab, and the tab shows the current state immediately (local, then
 * invalidated on save).
 */
export function AgentCapabilitiesTab({ agent }: Readonly<{ agent: AgentResponse }>) {
  const { t } = useTranslation("agents");
  const toast = useToast();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");

  const skillsQuery = useQuery(listAgentSkillsOptions({ path: { agent_uuid: agent.uuid } }));
  const allSkills = useQuery(listSkillsOptions({ query: { page_size: 100 } }));
  const allTools = useQuery(listToolsOptions({ query: { page_size: 100 } }));
  const allDataSources = useQuery(listDataSourcesOptions({ query: { page_size: 100 } }));
  const agentConnectors = useQuery(listAgentConnectorsOptions({ path: { agent_uuid: agent.uuid } }));
  const myConnectors = useQuery(listConnectorsOptions({ query: { page_size: 100 } }));
  const attachedConnectors = useMemo(() => agentConnectors.data?.data ?? [], [agentConnectors.data]);

  // Attached state (excludes "core" platform-extension capabilities — those are implicit).
  const attachedSkills = useMemo(
    () => (skillsQuery.data?.data ?? []).map((s) => s.skill).filter((s) => s.extension?.extensionType !== "platform"),
    [skillsQuery.data],
  );
  const attachedTools = useMemo(
    () => (agent.tools ?? []).filter((tool) => tool.extension?.extensionType !== "platform"),
    [agent.tools],
  );
  const attachedKnowledge = useMemo(() => agent.dataCollections ?? [], [agent.dataCollections]);

  // Local editable selection (uuid sets), initialized from the attached state.
  const [skillSel, setSkillSel] = useState<Set<string>>(new Set());
  const [toolSel, setToolSel] = useState<Set<string>>(new Set());
  const [knowledgeSel, setKnowledgeSel] = useState<Set<string>>(new Set());
  const [connectorSel, setConnectorSel] = useState<Set<string>>(new Set());
  const [connectorsReady, setConnectorsReady] = useState(false);
  if (!connectorsReady && agentConnectors.data) {
    setConnectorSel(new Set(attachedConnectors.map((c) => c.uuid)));
    setConnectorsReady(true);
  }
  const [initialized, setInitialized] = useState(false);
  if (!initialized && skillsQuery.data && agent.tools) {
    setSkillSel(new Set(attachedSkills.map((s) => s.uuid)));
    setToolSel(new Set(attachedTools.map((tool) => tool.uuid)));
    setKnowledgeSel(new Set(attachedKnowledge.map((dc) => dc.uuid)));
    setInitialized(true);
  }

  const canEdit = agent.canEdit ?? false;

  const dirty =
    !sameSet(skillSel, new Set(attachedSkills.map((s) => s.uuid))) ||
    !sameSet(toolSel, new Set(attachedTools.map((tool) => tool.uuid))) ||
    !sameSet(knowledgeSel, new Set(attachedKnowledge.map((dc) => dc.uuid))) ||
    !sameSet(connectorSel, new Set(attachedConnectors.map((c) => c.uuid)));

  const save = useMutation(updateAgentMutation());
  const attachSkillMutation = useMutation(attachSkillMutationFactory());

  const handleSave = async () => {
    try {
      // Skills: detach removed, attach added (per-skill endpoints).
      const currentSkills = new Set(attachedSkills.map((s) => s.uuid));
      await Promise.all(
        [...currentSkills].filter((uuid) => !skillSel.has(uuid)).map((uuid) => detachSkill(agent.uuid, uuid)),
      );
      const toAttach = [...skillSel].filter((uuid) => !currentSkills.has(uuid));
      if (toAttach.length > 0) {
        await attachSkillMutation.mutateAsync({
          path: { agent_uuid: agent.uuid },
          body: toAttach.map((skillUuid) => ({ skillUuid, isEnabled: true })),
          throwOnError: true,
        });
      }
      // Tools + knowledge: replace wholesale via the agent PATCH.
      await save.mutateAsync({
        path: { agent_uuid: agent.uuid },
        body: {
          toolConfigs: [...toolSel].map((toolUuid) => ({ toolUuid, isEnabled: true })),
          dataCollectionIds: [...knowledgeSel],
        },
        throwOnError: true,
      });
      // Connectors: replace wholesale (MHS-829), once we know what's attached.
      if (connectorsReady)
        await setAgentConnectors({
          path: { agent_uuid: agent.uuid },
          body: { connectorUuids: [...connectorSel] },
          throwOnError: true,
        });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: listAgentConnectorsQueryKey({ path: { agent_uuid: agent.uuid } }) }),
        queryClient.invalidateQueries({ queryKey: getAgentQueryKey({ path: { agent_uuid: agent.uuid } }) }),
        queryClient.invalidateQueries({ queryKey: listAgentSkillsQueryKey({ path: { agent_uuid: agent.uuid } }) }),
      ]);
      toast.success(t("capabilities.attached"));
    } catch (e) {
      toast.error(errorMessage(e, t("capabilities.saveFailed")));
    }
  };

  // Available options (excluding platform-extension capabilities), filtered by search.
  const q = search.trim().toLowerCase();
  const matches = (name: string, desc?: string | null) =>
    !q || name.toLowerCase().includes(q) || (desc ?? "").toLowerCase().includes(q);

  const availableSkills = (allSkills.data?.data ?? []).filter(
    (s) => s.extension?.extensionType !== "platform" && matches(s.name, s.description),
  );
  const availableTools = (allTools.data?.data ?? []).filter(
    (tool) => tool.extension?.extensionType !== "platform" && matches(tool.name, tool.description),
  );
  const availableKnowledge = (allDataSources.data?.data ?? []).filter((dc) => matches(dc.name, dc.description));

  return (
    <Card>
      <div className="flex flex-col gap-4">
        <Input
          leadingIcon={<Search />}
          placeholder={t("capabilities.search")}
          aria-label={t("capabilities.search")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Text variant="caption" tone="tertiary">
          {t("capabilities.baselineNote")}
        </Text>

        {/* Capabilities grouped by parent Extension. */}
        <ExtensionGroups
          skills={availableSkills}
          tools={availableTools}
          skillSel={skillSel}
          toolSel={toolSel}
          canEdit={canEdit}
          onToggleSkill={(uuid) => setSkillSel((s) => toggleIn(s, uuid))}
          onToggleTool={(uuid) => setToolSel((s) => toggleIn(s, uuid))}
        />

        {/* Connectors (MCP servers, APIs, your own integration accounts): all their tools at once. */}
        <ConnectorsSection
          available={(myConnectors.data?.data ?? []).filter((c) => matches(c.name, c.description))}
          attached={attachedConnectors}
          selected={connectorSel}
          canEdit={canEdit}
          onToggle={(uuid) => setConnectorSel((s) => toggleIn(s, uuid))}
        />

        {/* Skills without a parent extension, grouped by category. */}
        <UngroupedSkills
          skills={availableSkills.filter((s) => !s.extension)}
          skillSel={skillSel}
          canEdit={canEdit}
          onToggle={(uuid) => setSkillSel((s) => toggleIn(s, uuid))}
        />

        {/* Knowledge (data sources). */}
        <KnowledgeSection
          knowledge={availableKnowledge}
          knowledgeSel={knowledgeSel}
          canEdit={canEdit}
          onToggle={(uuid) => setKnowledgeSel((s) => toggleIn(s, uuid))}
        />

        {/* One save for the whole tab — pinned to the bottom of the scrolling
            content so it's always visible (not buried after the list). */}
        {canEdit && (
          <SaveBar dirty={dirty} saving={save.isPending} onSave={handleSave} saveLabel={t("capabilities.saveAction")} />
        )}
      </div>
    </Card>
  );
}

function sameSet(a: Set<string>, b: Set<string>): boolean {
  if (a.size !== b.size) return false;
  for (const x of a) if (!b.has(x)) return false;
  return true;
}

function toggleIn(set: Set<string>, uuid: string): Set<string> {
  const next = new Set(set);
  if (next.has(uuid)) next.delete(uuid);
  else next.add(uuid);
  return next;
}

function attachSkillMutationFactory() {
  return attachSkillsToAgentMutation();
}

async function detachSkill(agentUuid: string, skillUuid: string) {
  await detachSkillFromAgent({ path: { agent_uuid: agentUuid, skill_uuid: skillUuid }, throwOnError: true });
}
